import {CustomFileData, findIds, getLogger, wsfileActionMessage} from '@/shared/utils'
import {
  toastActionableConfig,
  toastDefaultConfig,
  toastInfiniteConfig,
} from '@/shared/config/toastConfig'
import {useQueryClient} from '@tanstack/react-query'
import {saveAs} from 'file-saver'
import JSZip from 'jszip'
import {useMemo, useRef, useState} from 'react'
import {Id, toast} from 'react-toastify'
import {useCognitoAuth} from '../../auth'
import {handleAuthError} from '../../auth/core/AuthErrorInterceptor'
import {fileQueryKeys, fileStoreFor, OWN_ROOT, StorageRoot} from '../core/storage'
import {dropShareCaches, isShareUnavailable, sharingQueryKeys, useShares} from '../../sharing'
import {shareErrorCode} from '../../sharing/errors'
import {
  firstLockedPath,
  isSharedFolderLocked,
  movesSharedFolder,
  sharedFolderLockedMessage,
} from '../../sharing/ownerLocks'
import {ShareMoveIntent, useOpenShareMove} from '../../sharing/ShareMoveDialog'
import {shareOperationErrorMessage} from '../../sharing/shareOperations'
import {useOpenShareTransfer} from '../../sharing/ShareTransferDialog'
import type {ShareTransferMode} from '../../sharing/shareFilesApi'
import {intoFolderMoves, usePinActions} from '../../pins'
import {sameRoot} from './fileBrowserRoot'
import {useFilesClipboard} from './FilesClipboardContext'
import {useSocket} from './SocketProvider'

const itemsLabel = (count: number) => (count === 1 ? '1 item' : `${count.toLocaleString()} items`)

/**
 * File actions for the selection: clipboard, paste, download, public links. `root` is the root the
 * selection is in (My Files by default); its store serves the download.
 */
const useFileTable = (
  selectedFiles: CustomFileData[],
  path: string[] = [''],
  root: StorageRoot = OWN_ROOT
) => {
  const {socket, updateInfiniteToast} = useSocket([], [])
  const zip = new JSZip()
  const queryClient = useQueryClient()
  const {currentUser} = useCognitoAuth()
  const identityId = currentUser?.identity_id || ''
  const store = useMemo(() => fileStoreFor(root, identityId), [root, identityId])
  const folderPath = useRef<string[]>(path)
  const [renamedFile, setRenamedFile] = useState<string>('')
  // Shared with every other useFileTable under the same Files browser (the keyboard shortcuts and
  // the context menu), so that what one copies the other can paste.
  const {filesToCopy, filesToMove, sourceRoot} = useFilesClipboard()
  const openTransfer = useOpenShareTransfer()
  const openShareMove = useOpenShareMove()
  const {sharedByMe} = useShares()
  const {followMoves} = usePinActions()
  const onCopy = () => {
    if (selectedFiles.length === 0) {
      toast('No files selected', {...toastDefaultConfig, type: 'warning'})
      return
    }
    filesToMove.current = []
    filesToCopy.current = selectedFiles
    sourceRoot.current = root
  }
  const onCut = () => {
    if (selectedFiles.length === 0) {
      toast('No files selected', {...toastDefaultConfig, type: 'warning'})
      return
    }

    filesToCopy.current = []
    filesToMove.current = selectedFiles
    sourceRoot.current = root
  }

  /**
   * A paste into another root than the one the files were copied in: between My Files and a shared
   * folder, which the Move/Copy dialog does through the sharing API (E8).
   */
  const pasteAcrossRoots = (
    from: StorageRoot,
    items: CustomFileData[],
    mode: ShareTransferMode,
    folderPath: string[]
  ) => {
    if (from.kind === 'share' && root.kind === 'share') {
      toast.warning(
        "Items can't go straight from one shared folder to another. Move them to My Files first.",
        toastDefaultConfig
      )
      return
    }
    const share = from.kind === 'share' ? from : root.kind === 'share' ? root : undefined
    if (!share || !openTransfer) {
      return
    }
    const destination = folderPath.slice(1).filter(Boolean).join('/')
    const toShare = root.kind === 'share'
    const locked = toShare
      ? firstLockedPath(
          items.map((file) => file.id),
          sharedByMe
        )
      : undefined
    openTransfer({
      direction: toShare ? 'toShare' : 'toMine',
      share: {
        shareId: share.id,
        name: share.name || share.folderPath.replace(/\/$/, '').split('/').pop() || '',
        folderPath: share.folderPath,
      },
      sources: items.map((file) => ({path: file.id, name: file.name, isDir: file.isDir})),
      destination: destination ? `${destination}/` : '',
      defaultMode: mode,
      moveBlockedReason: locked?.message,
    })
  }

  /** Copy or move inside the shared folder on screen: runs in the background, followed in a toast. */
  const pasteInShare = (mode: ShareTransferMode, items: CustomFileData[], folderPath: string[]) => {
    if (root.kind !== 'share') {
      return
    }
    const shareId = root.id
    const verb = mode === 'copy' ? 'Copying' : 'Moving'
    const toastId = toast.loading(`${verb} ${itemsLabel(items.length)}…`, {
      ...toastDefaultConfig,
      autoClose: false,
      closeOnClick: false,
    })
    const sources = items.map((file) => file.id)
    const destinationFolder = folderPath.slice(1).join('/')
    const run = mode === 'copy' ? store.copy : store.move
    run(sources, destinationFolder, {
      onProgress: (done, total) => toast.update(toastId, {render: `${verb}… ${done} of ${total}`}),
    })
      .then(() => {
        toast.update(toastId, {
          render: mode === 'copy' ? 'Copied' : 'Moved',
          type: 'success',
          isLoading: false,
          ...toastDefaultConfig,
        })
        // Pinned folders among them (or below them) keep their pins at the new place.
        if (mode === 'move') {
          followMoves(intoFolderMoves(sources, destinationFolder), shareId)
        }
      })
      .catch((err: unknown) => {
        getLogger().error(err)
        // Gone, or paused while its owner moves it: the share list says which.
        if (isShareUnavailable(err) || shareErrorCode(err) === 'share_moving') {
          void queryClient.invalidateQueries(sharingQueryKeys.shares())
        }
        toast.update(toastId, {
          render: `Couldn't ${mode} the items: ${shareOperationErrorMessage(err)}`,
          type: 'error',
          isLoading: false,
          ...toastActionableConfig,
        })
      })
      .finally(() => {
        void queryClient.invalidateQueries(fileQueryKeys.share(shareId))
      })
  }
  function sendWsMessage(
    action: string,
    destination: string | undefined,
    fileCount: string,
    customAttributes: any
  ) {
    const socketMessage: wsfileActionMessage = {
      source: 'client.omicsbox.web',
      message: {action, filePath: destination, ...customAttributes},
      fileCount,
    }
    if (socket?.readyState !== WebSocket.OPEN) {
      getLogger().info('message not sent, socket not open', socketMessage)
      return
    }
    getLogger().info('message sent', socketMessage)
    socket.send(JSON.stringify(socketMessage))
  }

  const onPaste = (folderPath: string[]) => {
    const copying = filesToCopy.current.length > 0
    const items = copying ? filesToCopy.current : filesToMove.current
    if (items.length > 0 && (!sameRoot(sourceRoot.current, root) || root.kind === 'share')) {
      if (sameRoot(sourceRoot.current, root)) {
        pasteInShare(copying ? 'copy' : 'move', items, folderPath)
      } else {
        pasteAcrossRoots(sourceRoot.current, items, copying ? 'copy' : 'move', folderPath)
      }
      filesToCopy.current = []
      filesToMove.current = []
      return
    }
    // The full key goes in the notification; the store takes the folder relative to the root.
    const destination = identityId + folderPath.join('/') + '/'
    const destinationFolder = folderPath.slice(1).join('/')
    let source: string[] = []
    if (filesToCopy.current.length > 0) {
      source = filesToCopy.current.map((file) => file.id)
      store
        .copy(source, destinationFolder)
        ?.then(() => {
          updateInfiniteToast(toast.loading('Copying files...', toastInfiniteConfig))
        })
        .catch((err: any) => {
          getLogger().error(err)
          handleAuthError(err)
          toast.error('Failed to copy files')
        })
      sendWsMessage(
        'copy',
        destination,
        countFiles(filesToCopy.current).toLocaleString(),
        undefined
      )
    } else if (filesToMove.current.length > 0) {
      source = filesToMove.current.map((file) => file.id)
      // The whole selection, in one request, when it goes through the sharing API (E10).
      const shareMove: ShareMoveIntent = {
        kind: 'move',
        items: filesToMove.current.map((file) => ({
          path: file.id,
          name: file.name,
          isDir: file.isDir,
        })),
        destination: destinationFolder === '' ? '' : `${destinationFolder}/`,
      }
      // E10: it carries a folder the user shares (or one above it). The sharing API moves it, and
      // the share follows, once the user confirms; the storage API would refuse it.
      if (root.kind === 'own' && openShareMove && movesSharedFolder(source, sharedByMe)) {
        openShareMove(shareMove)
        filesToCopy.current = []
        filesToMove.current = []
        return
      }
      const moved = source
      store
        .move(source, destinationFolder)
        ?.then(() => {
          updateInfiniteToast(toast.loading('Moving files...', toastInfiniteConfig))
          // Pinned folders among them (or below them) keep their pins at the new place.
          if (root.kind === 'own') {
            followMoves(intoFolderMoves(moved, destinationFolder))
          }
        })
        .catch((err: any) => {
          getLogger().error(err)
          handleAuthError(err)
          // E10, with an out-of-date share list: refetch it, and ask again through the sharing API.
          if (root.kind === 'own' && isSharedFolderLocked(err)) {
            void queryClient.invalidateQueries(sharingQueryKeys.shares())
            if (openShareMove) {
              openShareMove(shareMove)
            } else {
              toast.error(sharedFolderLockedMessage(err), toastActionableConfig)
            }
            return
          }
          toast.error('Failed to move files')
        })
      sendWsMessage(
        'move',
        destination,
        countFiles(filesToMove.current).toLocaleString(),
        undefined
      )
    }
    filesToCopy.current = []
    filesToMove.current = []
  }
  function countFiles(fileMap: CustomFileData[]): number {
    let fileIds = findIds(fileMap)
    let folders: string[] = []
    let files: string[] = []
    fileIds.forEach((file) => (file.endsWith('/') ? folders.push(file) : files.push(file)))
    let dynamoFiles: string[] = folders.filter(
      (folder) => !files.some((file) => file.startsWith(folder))
    )

    return files.length + dynamoFiles.length
  }
  const onShare = async () => {
    await store.createPublicLinks?.(selectedFiles.map((file) => file.id))
    queryClient.invalidateQueries(fileQueryKeys.all)
  }

  const onDownload = async () => {
    const fileNames = findIds(selectedFiles).filter((id) => !id.endsWith('/'))
    let t: Id | undefined = undefined
    let filesDownloaded: (string | Blob)[]
    try {
      filesDownloaded = await Promise.all(
        fileNames.map((file) => store.download(file, fileNames.length > 1))
      )
    } catch (error) {
      getLogger().error('Download failed:', error)
      if (root.kind === 'share' && isShareUnavailable(error)) {
        // Its listings and URLs go; the browser notices on its next listing and leaves the folder.
        dropShareCaches(queryClient, root.id)
        toast.error('That shared folder is no longer available to you.', toastDefaultConfig)
      } else {
        toast.error('Download failed', toastDefaultConfig)
      }
      return
    }
    toast('Files downloaded!', {...toastDefaultConfig, type: toast.TYPE.SUCCESS})
    if (typeof filesDownloaded[0] === 'string') {
      window.open(filesDownloaded[0])
    } else {
      for (let index = 0; index < fileNames.length; index++) {
        const fileName = fileNames[index]
        const content = filesDownloaded[index]
        zip.file(fileName, content)
      }
      zip
        .generateAsync({type: 'blob'}, (p_1) => {
          const progress = p_1.percent / 100
          if (t === undefined) {
            t = toast('Compressing...', {
              ...toastDefaultConfig,
              hideProgressBar: false,
              closeOnClick: false,
              closeButton: false,
              progress,
            })
          } else {
            toast.update(t, {
              progress,
            })
          }
        })
        .then((blob) => {
          setTimeout(() => {
            if (t) {
              toast.done(t)
            }
            toast.dismiss()
            toast.clearWaitingQueue()
          }, 100)

          saveAs(
            blob,
            `omicsbox-download-${new Date().toISOString().substring(0, 19).replace(/[-:]/g, '')}`
          )
        })
        .catch((error) => getLogger().error(error))
    }
  }

  const onUnshare = () => {
    const filePaths = selectedFiles.map((file) => file.id)

    store
      .removePublicLinks?.(filePaths)
      .then((unshareResponse: any) => {
        toast(`Objects unshared: ${unshareResponse.objectsUnshared.join(', ')}`, toastDefaultConfig)
        queryClient.invalidateQueries(fileQueryKeys.all)
      })
      .catch((e: any) => getLogger().error(e))
  }

  return {
    onCopy,
    onCut,
    filesToCopy,
    filesToMove,
    onPaste,
    onShare,
    onDownload,
    renamedFile,
    setRenamedFile,
    onUnshare,
    folderPath,
    countFiles,
    sendWsMessage,
  }
}

export default useFileTable
